import {useEffect, useState} from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTheme} from '@mui/material/styles';
import {CompanyAnnual, getAnnual} from '@/services/accountService/companyAnnualService';
import {DenseTable, ExpansionPanel, Labeled, RangeLabeled} from '@/components/common';

interface Props {
    open: boolean;
    onClose: () => void;
    annualId?: number;
}

export default function AnnualDetail({open, onClose, annualId}: Props) {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<CompanyAnnual | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        if (!annualId) {
            setDetail(null);
            return;
        }
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getAnnual(annualId);
                setDetail(data);
            } catch (e: any) {
                setError(e?.message || 'Failed to load annual detail');
            } finally {
                setLoading(false);
            }
        })();
    }, [open, annualId]);

    const renderDetail = (d: CompanyAnnual) => (
        <Box sx={{pt: 1}}>
            <ExpansionPanel title="Period (期間)" defaultExpanded>
                <Grid container spacing={2}>
                    <Grid size={6}><RangeLabeled label="Current Year / Period" from={d.periodFrom}
                                                 to={d.periodTo}/></Grid>
                    <Grid size={6}>
                        {(d.lastYearPeriodFrom !== null && d.lastYearPeriodTo !== null) && (
                            <>
                                <RangeLabeled label="Last Year / Period" from={d.lastYearPeriodFrom}
                                              to={d.lastYearPeriodTo}/>
                            </>
                        )}
                    </Grid>
                    <Grid size={6}><Labeled label="Year Of Assessment (報稅年份)"
                                            value={d.yearOfAssessment}/></Grid>
                    <Grid size={6}>
                        <Labeled
                            label="Over/ Within 18 months? (18個月內/超過)"
                            value={
                                d.auditMonths == null
                                    ? undefined
                                    : d.auditMonths === 1
                                        ? 'Over'
                                        : 'Within'
                            }
                        />
                    </Grid>
                    <Grid size={6}>
                        <Labeled
                            label="Cessation during this period? (本年度內申請結業?)"
                            value={
                                d.cessationDuringThisPeriod == null
                                    ? undefined
                                    : d.cessationDuringThisPeriod === 1
                                        ? 'Yes'
                                        : 'No'
                            }
                        />
                    </Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Bank Confirmations (銀行詢證)">
                <Paper variant="outlined" sx={{p: 1}}>
                    <DenseTable
                        head={["Bank", "Account Number", "Status"]}
                        rows={(d.bankConfirmations || []).map(b => [
                            b.bankName,
                            b.accountNumber,
                            b.isValid === 1 ? 'Inactive' : 'Active',
                        ])}
                    />
                </Paper>
            </ExpansionPanel>

            <ExpansionPanel title="Company Details (公司詳情)">
                <Grid container spacing={2}>
                    <Grid size={12}><Labeled label="Overseas Profits Claims (海外利得申索)"
                                             value={d.offShoreClaim == null ? undefined
                                                 : d.offShoreClaim === 1 ? 'Yes' : 'No'}/></Grid>
                    <Grid size={12}>
                        <Labeled
                            label="Is it limited by guarantee？ (是否擔保有限公司?)"
                            value={
                                d.limitByGuarantee == null
                                    ? undefined
                                    : d.limitByGuarantee === 1
                                        ? 'Yes'
                                        : 'No'
                            }
                        />
                    </Grid>
                    {/* Removed: inCorpDate not part of CompanyAnnual */}
                    <Grid size={6}><Labeled label="Tax File No (商業登記證號碼)"
                                            value={d.taxFileNo}/></Grid>
                    {d.limitByGuarantee === 1 && (
                        <>

                            {/*<Grid size={12}>*/}
                            {/*    <DenseTable label="Liability of each member (每位成員的擔保金額)"*/}
                            {/*                head={['Member', 'Currency', 'Amount']}*/}
                            {/*                rows={(d.liabilities || []).map(it => [it.member, it.currency, it.amount])}*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            <Grid size={6}><Labeled
                                label="Any name other than Company? (有'公司'外的其他稱謂?)"
                                value={d.otherNameThanCompany}/></Grid>
                            <Grid size={6}><Labeled label="Is it section 88? (是否慈善團體?)"
                                                    value={d.section88}/></Grid>
                        </>
                    )}
                    {/* Removed: registeredOffice not part of CompanyAnnual */}
                    <Grid size={12}><Labeled label="Change of address? (是否改註冊地址)"
                                             value={d.changeOfAddress == null ? undefined
                                                 : d.changeOfAddress === 1 ? 'Yes' : 'No'}/></Grid>
                    {d.changeOfAddress === 1 && (
                        <>
                            <Grid size={6}><Labeled label="Change Date (更新日期)"
                                                    value={d.changeDate}/></Grid>
                            <Grid size={6}><Labeled label="Last Address (上次地址)"
                                                    value={d.lastAddress}/></Grid>
                        </>
                    )}
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Audit Opinion (審計意見)">
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Labeled
                            label="Qualify opinion? (審計意見)"
                            value={
                                d.qualifyOpinion == null
                                    ? undefined
                                    : d.qualifyOpinion === 1
                                        ? 'qualify opinion (保留意見)'
                                        : 'clean (無保留意見)'
                            }
                        />
                    </Grid>
                    <Grid size={12}>
                        <Labeled
                            label="First audit? (首次審計)"
                            value={
                                d.firstAudit == null
                                    ? undefined
                                    : d.firstAudit === 1
                                        ? 'Yes'
                                        : 'No'
                            }
                        />
                    </Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Other Disclosures (其他披露)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Fixed Assets - PPE (有否固定資產)"
                                            value={d.ppe == null ? undefined
                                                : d.ppe === 1 ? 'Yes' : 'No'}/></Grid>
                    <Grid size={6}><Labeled label="Related Party Transactions (有否關聯公司交易)"
                                            value={d.relatedPartyTransactions == null ? undefined
                                                : d?.relatedPartyTransactions === 1 ? 'Yes' : 'No'}/></Grid>
                    <Grid size={6}><Labeled label="Rental commitment (有否租賃承擔)"
                                            value={d.rentalCommitment == null ? undefined
                                                : d?.rentalCommitment === 1 ? 'Yes' : 'No'}/></Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Holding Structure (控股結構)">
                <Grid container spacing={2}>
                    <Grid size={12}><Labeled label="Has holding co.? (股東是否控股公司?)"
                                             value={d.holdingCo == null ? undefined :
                                                 d.holdingCo === 1 ? 'Yes' : 'No'}/></Grid>
                    {d.holdingCo === 1 && (
                        <>
                            <Grid size={6}><Labeled label="Name of immediate holding co. (直接控股公司名稱)"
                                                    value={d.nameOfImmediate}/></Grid>
                            <Grid size={6}><Labeled
                                label="Immediate holding - place of incoporation (直接控股公司成立國家)"
                                value={d.immediateHolding}/></Grid>
                            <Grid size={6}><Labeled label="Name of ultimate holding co. (最終控股公司名稱)"
                                                    value={d.nameOfUltimate}/></Grid>
                            <Grid size={6}><Labeled
                                label="Ultimate holding - place of incoporation (最終控股公司成立國家)"
                                value={d.ultimateHolding}/></Grid>
                        </>
                    )}
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Allotment Of Shares (增資)">
                <Grid container spacing={2}>
                    <Grid size={12}><Labeled
                        label="Increase Capital of Co. (公司是否增資)"
                        value={d.allotmentOfShares == null ? undefined
                            : d?.allotmentOfShares === 1 ? 'Yes' : 'No'}/></Grid>
                    {d?.allotmentOfShares === 1 && (
                        <>
                            <Grid size={6}><Labeled label="No. of Shares (股份数目)"
                                                    value={d.noOfShares}/></Grid>
                            <Grid size={6}><Labeled label="Price of Each Shares (每股金額)"
                                                    value={d.pricePerShare}/></Grid>
                            <Grid size={6}><Labeled label="Total Allotment (增資金額)"
                                                    value={d.allotmentAmount}/></Grid>
                            <Grid size={6}><Labeled label="Price Currency (價格貨幣)"
                                                    value={d.priceCurrency}/></Grid>
                        </>
                    )}
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Shareholding (股東)">
                <DenseTable label=""
                            head={["Shareholder's Name", "Gender"]}
                            rows={(d.shareholders || []).map(it => [it.shareholderName, it.gender === 0 ? 'Male' : 'Female',])}
                />
            </ExpansionPanel>

            <ExpansionPanel title="Dividend (派息)">
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Labeled
                            label="Dividend paid? (是否派息?)"
                            value={
                                d.dividendPaid == null ? undefined
                                    : d.dividendPaid === 0 ? 'No Dividend Paid (沒有派股息)'
                                        : d.dividendPaid === 1 ? 'Year End Dividend (有派年結股息)'
                                            : 'Interm Dividend (有派中期股息)'
                            }
                        />
                    </Grid>
                    {(d.dividendPaid === 1 || d.dividendPaid === 2) && (
                        <>
                            <Grid size={6}><Labeled label="Dividend Currency (派股息的貨幣)"
                                                    value={d.dividendCurrency}/></Grid>
                            <Grid size={6}><Labeled label="Amount per each share (每股派息的金額)"
                                                    value={d.amountPerEachShare}/></Grid>
                            <Grid size={6}><Labeled label="Total money amount of dividend paid (總派息金額)"
                                                    value={d.totalDividendAmount}/></Grid>
                        </>
                    )}
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Company Name & Auditor Changes (公司名稱與審計師變更)">
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Labeled
                            label="Change of Co. name (是否改公司名称?)"
                            value={
                                d.isChangeName == null
                                    ? undefined
                                    : d.isChangeName === 1
                                        ? 'Yes'
                                        : 'No'
                            }
                        />
                    </Grid>
                    {d.isChangeName === 1 && (
                        <>
                            <Grid size={12}><Labeled label="Since when? (改變生效日期)"
                                                     value={d.changeEffectiveDate}/></Grid>
                            <Grid size={6}><Labeled label="Old Co. Name (舊公司名稱)"
                                                    value={d.oldCompanyName}/></Grid>
                            <Grid size={6}><Labeled label="Old Co. Chinese Name (舊公司中文名稱)"
                                                    value={d.oldCompanyChineseName}/></Grid>
                        </>
                    )}

                    <Grid size={6}>
                        <Labeled
                            label="Change of financial year end? (有否改會計年結日?)"
                            value={
                                d.changeOfFinancialYearEnd == null
                                    ? undefined
                                    : d.changeOfFinancialYearEnd === 1
                                        ? 'Yes'
                                        : 'No'
                            }
                        />
                    </Grid>
                    <Grid size={6}><Labeled label="Date of board resolution? (董事局議程的日期?)"
                                            value={d.dateOfBoardResolution}/></Grid>
                    {d.changeOfFinancialYearEnd === 1 && (
                        <Grid size={12}><Labeled label="Reason to change? (改變的原因?)"
                                                 value={d.reasonToChange}/></Grid>
                    )}
                    <Grid size={12}><Labeled label="Name of former auditor (上手會計師名稱)"
                                             value={d.formerAuditor}/></Grid>
                    <Grid size={12}><Labeled label="Name of former address auditor (上手会计师地址)"
                                             value={d.addressOfFormerAuditor}/></Grid>
                    <Grid size={12}><Labeled label="Professional clearance (專業認可函件)"
                                             value={d.professionalClearance}/></Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Business (業務)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Principal activities (主營業務)"
                                            value={d.principalActivities == null ? undefined
                                                : d.principalActivities === 1 ? 'General Trading'
                                                    : d.principalActivities === 2 ? 'Services'
                                                        : d.principalActivities === 3 ? 'Trading and Service' :
                                                            'InActive'}/></Grid>
                    <Grid size={6}><Labeled label="Business Nature (業務性質)"
                                            value={d.businessNature}/></Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Currency & Exchange Rate (貨幣與匯率)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Persentation Currency (報告貨幣)"
                                            value={d.presentationCurrency}/></Grid>
                    {d.presentationCurrency === 'HKD' ? null : (
                        <Grid size={6}><Labeled label="Exchange Rate (匯率)"
                                                value={d.exchangeRate}/></Grid>
                    )}
                    <Grid size={6}><Labeled label="Functional currency (公司的功能性貨幣)"
                                            value={d.functionalCurrency}/></Grid>
                </Grid>
            </ExpansionPanel>

            {/*<ExpansionPanel title="Directors & Shareholders (董事與股東)">*/}
            {/*    <Grid container spacing={2}>*/}
            {/*        <Grid size={12}>*/}
            {/*            <Typography variant="body2" gutterBottom sx={{display: 'block'}}>*/}
            {/*                Directors (董事)*/}
            {/*            </Typography>*/}
            {/*        </Grid>*/}
            {/*        <Grid size={12}>*/}
            {/*            <Paper variant="outlined" sx={{p: 1}}>*/}
            {/*                <DenseTable*/}
            {/*                    head={['Name', 'Appointment Date', 'Resign Date', 'ID Number', 'Address']}*/}
            {/*                    rows={(d.directors || []).map(d => [d.name, d.appointmentDate, d.resignDate, d.idNumber, d.address])}*/}
            {/*                />*/}
            {/*            </Paper>*/}
            {/*        </Grid>*/}
            {/*        <Grid size={12}>*/}
            {/*            <Typography variant="body2" gutterBottom sx={{display: 'block'}}>*/}
            {/*                Shareholders (股東)*/}
            {/*            </Typography>*/}
            {/*        </Grid>*/}
            {/*        // TODO need to change the list value*/}
            {/*        <Grid size={12}>*/}
            {/*            <Paper variant="outlined" sx={{p: 1}}>*/}
            {/*                <DenseTable*/}
            {/*                    head={['Name', 'No. Of Shares','Currency', 'Price per share','Exchange Rate', 'Total']}*/}
            {/*                    rows={(d.shareholders || []).map(s => [s.name, s.noOfShares,'USD', s.noOfShares,'7.8', 'HKD ' +s.noOfShares])}*/}
            {/*                />*/}
            {/*            </Paper>*/}
            {/*        </Grid>*/}
            {/*    </Grid>*/}
            {/*</ExpansionPanel>*/}

            <ExpansionPanel title="Small Cash Confirmation (小額現金詢證)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Cash Confirmation (現金詢證函)"
                                            value={d.pettyCashConfirmation}/></Grid>
                    <Grid size={6}><Labeled label="Cash Confirmation Amount (現金詢證金額)"
                                            value={d.pettyCashAmount}/></Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Tax (稅務)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="This year two tired tax? (今年有否二級稅率)"
                                            value={d.twoTierTaxThisYear == null ? undefined
                                                : d.twoTierTaxThisYear === 1 ? 'Yes' : 'No'
                                            }/></Grid>
                    <Grid size={6}><Labeled label="Last Year two tired tax? (上年有否二級稅率)"
                                            value={d.twoTierTaxLastYear == null ? undefined
                                                : d.twoTierTaxLastYear === 1 ? 'Yes' : 'No'}/></Grid>
                    <Grid size={6}>
                        <Labeled
                            label="Assessable profit/adjusted loss (應評稅利潤/應評稅虧損)"
                            value={
                                d.assessableResultType == null
                                    ? undefined
                                    : d.assessableResultType === 0
                                        ? '+'
                                        : '-'
                            }
                        />
                    </Grid>
                    <Grid size={6}><Labeled label="Assessable amount (應評稅金額)"
                                            value={d.assessableAmount}/></Grid>
                    <Grid size={6}><Labeled label="Tax loss B/F (稅務虧損 B/F)"
                                            value={d.taxLossBF}/></Grid>
                    <Grid size={6}><Labeled label="Tax loss C/F (稅務虧損 C/F)"
                                            value={d.taxLossCF}/></Grid>
                </Grid>
            </ExpansionPanel>

            {/*<ExpansionPanel title="Dividends to overseas shareholders (海外股东派息)">*/}
            {/*    <Grid container spacing={2}>*/}
            {/*        <Grid size={12}><Labeled label="Dividend Paid of Overseas (是否发放海外股息)"*/}
            {/*                                 value={d.dividendPaidOfOverseas == null ? undefined*/}
            {/*                                     : d.dividendPaidOfOverseas === 0 ? 'No Dividend Paid (沒有派股息)'*/}
            {/*                                         : d.dividendPaidOfOverseas === 1 ? 'Year End Dividend (有派年結股息)'*/}
            {/*                                             : 'Interm Dividend (有派中期股息)'}/></Grid>*/}
            {/*        {(d.dividendPaidOfOverseas === 1 || d.dividendPaidOfOverseas === 2) && (*/}
            {/*            <>*/}
            {/*                <Grid size={6}><Labeled label="Overseas Dividend Currency (海外股息货币)"*/}
            {/*                                        value={d.overseasDividendCurrency}/></Grid>*/}
            {/*                <Grid size={6}><Labeled*/}
            {/*                    label="Per Share of Overseas Dividend (每股派息金额-海外)"*/}
            {/*                    value={d.overseasDividendAmountPerShare}/></Grid>*/}
            {/*                <Grid size={6}><Labeled*/}
            {/*                    label="Total Dividend Amount of Overseas (海外总派息金额)"*/}
            {/*                    value={d.overseasTotalDividendAmount}/></Grid>*/}
            {/*            </>*/}
            {/*        )}*/}
            {/*    </Grid>*/}
            {/*</ExpansionPanel>*/}

            {/*<ExpansionPanel title="Net Debt (淨負債)">*/}
            {/*    <Grid container spacing={2}>*/}
            {/*        <Grid size={12}><Labeled label="Net Debt (净负债)"*/}
            {/*                                 value={d.netLiabilities}/></Grid>*/}
            {/*    </Grid>*/}
            {/*</ExpansionPanel>*/}

            <ExpansionPanel title="Audit Staffs (審計人員)">
                <Paper variant="outlined" sx={{p: 1}}>
                    <DenseTable
                        head={['Staff Name', 'Position', 'Initial', 'Role']}
                        rows={(d.auditStaffs || []).map(s => [
                            s.auditStaffName,
                            s.post,
                            s.initial,
                            s.roleType === 1 ? 'Preparer' : s.roleType === 2 ? 'Reviewer' : s.roleType === 3 ? 'Partner' : String(s.roleType ?? '')
                        ])}
                    />
                </Paper>
            </ExpansionPanel>

            <ExpansionPanel title="Auditors (審計師)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Audit Certificate ID"
                                            value={d.auditCertificateId}/></Grid>
                    <Grid size={6}><Labeled label="IRD Letter signer (稅局信的簽署人名稱)"
                                            value={d?.irdLetterSigner}/></Grid>
                </Grid>
            </ExpansionPanel>

            <ExpansionPanel title="Other (其他)">
                <Grid container spacing={2}>
                    <Grid size={6}><Labeled label="Referrer (審計工作介紹人)"
                                            value={d.referrer}/></Grid>
                    <Grid size={6}><Labeled label="In-Charge (審計工作負責人)"
                                            value={d.inCharge}/></Grid>
                </Grid>
            </ExpansionPanel>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            sx={{'& .MuiDialog-paper': {bgcolor: theme.palette.background.paper, boxShadow: theme.shadows[3]}}}
        >
            <DialogTitle>
                Annual Detail
                <IconButton aria-label="close" onClick={onClose} sx={{position: 'absolute', right: 8, top: 8}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{bgcolor: theme.palette.background.default}}>
                {loading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                        <CircularProgress color="secondary"/>
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : !detail ? (
                    <Typography color="text.secondary">No data</Typography>
                ) : (
                    renderDetail(detail)
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="primary"
                        sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
